import React from 'react';
import { useTranslation } from 'react-i18next';

import { Plus } from 'lucide-react';

import { Product } from '../../types/catalogue';
import { resolveImageSrc } from '../../utils/imageUtils';
import { LoadingSpinner } from '../loading/LoadingSpinner';

/**
 * Get the best available image source for a product.
 */
const getProductImageSrc = (product: Product): string | null => {
  return resolveImageSrc(product.imageUrl);
};

interface ProductGridViewProps {
  products: Product[];
  selectedProductId: string | null;
  onProductSelect: (productId: string) => void;
  onAddProduct?: () => void;
  isLoading?: boolean;
}

const ProductGridView: React.FC<ProductGridViewProps> = ({
  products,
  selectedProductId,
  onProductSelect,
  onAddProduct,
  isLoading,
}) => {
  const { t } = useTranslation();

  // Loading state
  if (isLoading) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-greyP3">
        <LoadingSpinner size={40} color="#B3EC49" />
      </div>
    );
  }

  // Empty state with Add Product button
  if (products.length === 0) {
    return (
      <div className="flex-1 flex flex-col items-center justify-center text-greyP3 gap-4">
        <p className="text-lg">{t('catalogue.noProducts')}</p>
        {onAddProduct && (
          <button
            onClick={onAddProduct}
            className="flex items-center gap-2 px-6 py-3 rounded-full bg-green01 text-black text-sm font-medium
              hover:bg-green03 transition-colors"
          >
            <Plus className="w-5 h-5" />
            {t('catalogue.addProduct')}
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto scrollbar-hide bg-black p-6">
      <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
        {/* Add New Product Card - First item in the grid */}
        {onAddProduct && (
          <div
            onClick={onAddProduct}
            className="group cursor-pointer rounded-lg overflow-hidden transition-all duration-200
              border-2 border-zinc-900 hover:border-green01 hover:bg-zinc-900/50"
          >
            {/* Placeholder area with Plus icon */}
            <div className="aspect-square p-4 flex flex-col items-center justify-center gap-3">
              <div className="w-16 h-16 rounded-full bg-zinc-800 group-hover:bg-green01/20 flex items-center justify-center transition-colors">
                <Plus className="w-8 h-8 text-zinc-500 group-hover:text-green01 transition-colors" />
              </div>
              <span className="text-zinc-500 group-hover:text-green01 text-sm font-medium transition-colors">
                {t('catalogue.addProduct')}
              </span>
            </div>

            {/* Separator Line */}
            <div className="mx-4 border-t border-zinc-700" />

            {/* Info placeholder */}
            <div className="p-4">
              <h3 className="text-zinc-600 font-semibold text-sm">{t('catalogue.newProduct')}</h3>
              <p className="text-zinc-700 text-xs mt-1">{t('catalogue.clickToCreate')}</p>
            </div>
          </div>
        )}

        {/* Existing Product Cards */}
        {products.map((product) => {
          const isSelected = selectedProductId === product.id;
          const imageSrc = getProductImageSrc(product);

          return (
            <div
              key={product.id}
              onClick={() => onProductSelect(product.id)}
              className={`
                group cursor-pointer rounded-lg overflow-hidden transition-all duration-200
                 border-2 border-zinc-900  hover:border-green01 hover:bg-zinc-900/50
                ${isSelected ? 'bg-zinc-800 ring-1 ring-zinc-600' : 'bg-transparent hover:bg-zinc-900'}
              `}
            >
              {/* Product Image */}
              <div className="aspect-square flex items-center justify-center bg-black rounded-t-lg overflow-hidden">
                {imageSrc ? (
                  <img
                    src={imageSrc}
                    alt={product.name}
                    className="w-full h-full object-contain p-4"
                    onError={(e) => {
                      // On error, hide the image and show the black fallback
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="w-full h-full bg-black flex items-center justify-center">
                    <span className="text-zinc-700 text-xs">{t('catalogue.noImage')}</span>
                  </div>
                )}
              </div>

              {/* Separator Line */}
              <div className="mx-4 border-t border-zinc-700" />

              {/* Product Info */}
              <div className="p-4">
                <h3 className="text-white font-semibold text-sm truncate py-0.5">{product.name}</h3>
                {product.subtitle && <p className="text-zinc-500 text-xs mt-1 truncate py-0.5">{product.subtitle}</p>}
                {product.description && (
                  <p className="text-zinc-500 text-xs mt-1 line-clamp-2">{product.description}</p>
                )}
                {product.variants.length > 0 && (
                  <p className="text-zinc-600 text-xs mt-2">
                    {product.variants.length} {product.variants.length === 1 ? 'variant' : 'variants'}
                  </p>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProductGridView;
